import type { ReactNode } from "react";

/**
 * AtmosphereShell — the stadium atmosphere (SEASON ONE Loop 0 spine; ported
 * from the prototype's .stadium block). Cheap gradients + grain, NO images:
 * sky radial, stands strip, two sweeping floodlight beams, volt pitch glow,
 * bokeh crowd lights, feTurbulence grain overlay. Pure CSS — this is the app
 * shell that paints instantly from the service worker.
 *
 * `beams={false}` drops the two sweeping beams for the LITE device tier.
 * Children render in the content layer (z content) above the atmosphere and
 * below the grain, exactly per the tokens z-stack.
 */

/** Deterministic bokeh field (prototype spawned 26 random dots via JS; this is
 * the CSS-only, capped-intensity port — fixed positions, no hydration drift).
 * delay is a negative fraction of the twinkle loop so the field is desynced. */
const BOKEH: ReadonlyArray<{
  left: number;
  top: number;
  size: number;
  delay: number;
}> = [
  { left: 4, top: 21, size: 3, delay: 0.11 },
  { left: 11, top: 33, size: 5, delay: 0.58 },
  { left: 17, top: 24, size: 2, delay: 0.32 },
  { left: 24, top: 36, size: 4, delay: 0.83 },
  { left: 31, top: 20, size: 3, delay: 0.47 },
  { left: 37, top: 29, size: 6, delay: 0.09 },
  { left: 44, top: 23, size: 2, delay: 0.71 },
  { left: 50, top: 35, size: 4, delay: 0.26 },
  { left: 56, top: 26, size: 3, delay: 0.94 },
  { left: 63, top: 32, size: 5, delay: 0.4 },
  { left: 69, top: 19, size: 2, delay: 0.65 },
  { left: 75, top: 30, size: 4, delay: 0.18 },
  { left: 81, top: 25, size: 3, delay: 0.52 },
  { left: 87, top: 37, size: 5, delay: 0.77 },
  { left: 92, top: 22, size: 2, delay: 0.36 },
  { left: 97, top: 31, size: 4, delay: 0.88 },
  { left: 8, top: 27, size: 2, delay: 0.22 },
  { left: 60, top: 21, size: 3, delay: 0.61 },
];

export interface AtmosphereShellProps {
  /** Sweeping floodlight beams — turn off for the LITE device tier. Default on. */
  beams?: boolean;
  children?: ReactNode;
  className?: string;
}

export function AtmosphereShell({
  beams = true,
  children,
  className,
}: AtmosphereShellProps) {
  return (
    <div className={["mdui-stadium", className].filter(Boolean).join(" ")}>
      <div className="mdui-stadium-bg" aria-hidden="true">
        <div className="mdui-stadium-sky" />
        <div className="mdui-stadium-stands" />
        {beams ? (
          <>
            <div className="mdui-stadium-beam mdui-stadium-beam--1" />
            <div className="mdui-stadium-beam mdui-stadium-beam--2" />
          </>
        ) : null}
        <div className="mdui-stadium-pitchglow" />
        {BOKEH.map((b, i) => (
          <span
            key={i}
            className="mdui-stadium-bokeh"
            style={{
              width: b.size,
              height: b.size,
              left: `${b.left}%`,
              top: `${b.top}%`,
              animationDelay: `calc(var(--md-time-twinkle) * -${b.delay})`,
            }}
          />
        ))}
      </div>
      <div className="mdui-stadium-content">{children}</div>
      <div className="mdui-stadium-grain" aria-hidden="true" />
    </div>
  );
}
